import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  SparklesIcon,
  AcademicCapIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';

// Mock data for demonstration
const mockSkills = [
  { id: 1, name: 'John Doe', department: 'Engineering', role: 'Frontend Developer', skills: { 'React': 4, 'JavaScript': 5, 'UI/UX Design': 3, 'Backend (Node.js)': 2 } },
  { id: 2, name: 'Jane Smith', department: 'Engineering', role: 'Backend Developer', skills: { 'React': 2, 'JavaScript': 4, 'UI/UX Design': 1, 'Backend (Node.js)': 5 } },
  { id: 3, name: 'Mike Johnson', department: 'Marketing', role: 'Digital Marketer', skills: { 'Content Creation': 5, 'SEO': 4, 'Data Analysis': 3, 'Social Media': 5 } },
  { id: 4, name: 'Emily Davis', department: 'Sales', role: 'Sales Manager', skills: { 'Communication': 5, 'Negotiation': 5, 'CRM Software': 4, 'Leadership': 4 } },
];

// Define skill levels for display
const skillLevels = {
  1: 'Novice',
  2: 'Beginner',
  3: 'Competent',
  4: 'Proficient',
  5: 'Expert',
};

const SkillMatrix = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('All');

  const departments = ['All', ...new Set(mockSkills.map(s => s.department))];

  const filteredSkills = mockSkills.filter(
    (employee) => selectedDepartment === 'All' || employee.department === selectedDepartment
  );

  const getSkillColor = (level) => {
    if (level === 5) return 'bg-purple-200 text-purple-800';
    if (level === 4) return 'bg-indigo-200 text-indigo-800';
    if (level === 3) return 'bg-blue-200 text-blue-800';
    if (level === 2) return 'bg-yellow-200 text-yellow-800';
    return 'bg-gray-200 text-gray-800';
  };

  const getCommonSkills = () => {
    if (filteredSkills.length === 0) return [];
    const allSkills = filteredSkills.flatMap(s => Object.keys(s.skills));
    return [...new Set(allSkills)];
  };

  const commonSkills = getCommonSkills();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8 text-gray-800">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center max-w-4xl mx-auto mb-10"
      >
        <h1 className="text-4xl md:text-5xl font-extrabold text-indigo-800">Skill Matrix & Competency Mapping</h1>
        <p className="text-lg md:text-xl font-light text-indigo-600 mt-2">
          Visualize team skills and identify development opportunities.
        </p>
      </motion.div>

      <div className="max-w-7xl mx-auto bg-white p-6 rounded-2xl shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Team Skills Overview</h2>
          <Menu as="div" className="relative inline-block text-left">
            <div>
              <Menu.Button className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500">
                Department: {selectedDepartment}
                <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
              </Menu.Button>
            </div>
            <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
              <div className="py-1">
                {departments.map((dept) => (
                  <Menu.Item key={dept}>
                    {({ active }) => (
                      <button
                        onClick={() => setSelectedDepartment(dept)}
                        className={`${
                          active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                        } block w-full text-left px-4 py-2 text-sm`}
                      >
                        {dept}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Menu>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                {commonSkills.map((skill) => (
                  <th key={skill} scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {skill}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSkills.length > 0 ? (
                filteredSkills.map((employee) => (
                  <motion.tr
                    key={employee.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <AcademicCapIcon className="h-6 w-6 text-indigo-500 mr-2" />
                        <span className="text-sm font-medium text-gray-900">{employee.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {employee.role}
                    </td>
                    {commonSkills.map((skill) => {
                      const level = employee.skills[skill] || 1;
                      return (
                        <td key={skill} className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getSkillColor(level)}`}>
                            {skillLevels[level]}
                          </span>
                        </td>
                      );
                    })}
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan={commonSkills.length + 2} className="px-6 py-4 text-center text-sm text-gray-500">
                    No employees found for this department.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-8">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Skill Level Legend</h3>
          <ul className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(skillLevels).map(([level, name]) => (
              <li key={level} className="flex items-center space-x-2">
                <span className={`w-4 h-4 rounded-full ${getSkillColor(parseInt(level))}`}></span>
                <span className="text-sm font-medium text-gray-600">{name} ({level})</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SkillMatrix;